import { useState, useRef, useEffect } from "react";
import EmojiPicker, { Theme } from "emoji-picker-react";
import { File, FileText, Images, Plus, Smile } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import type { EmojiClickData } from "emoji-picker-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { AudioRecorder } from "./AudioRecorder";
import { TemplatesMenu } from "./TemplatesMenu";
import { useTemplates } from "@/hooks/useTemplates";
import type { Database } from "@/lib/supabase";

type AccountTemplate = Database['public']['Tables']['account_templates']['Row']

interface MessageInputProps {
  message: string;
  setMessage: (message: string) => void;
  onSendMessage: () => void;
  onSendAudio: (audioUrl: string) => Promise<boolean>;
  onSendImage: (imageBase64: string, caption?: string, fileName?: string) => Promise<boolean>;
  onSendDocument: (documentBase64: string, fileName: string, caption?: string) => Promise<boolean>;
  sending?: boolean;
}

export const MessageInput = ({ message, setMessage, onSendMessage, onSendAudio, onSendImage, onSendDocument, sending = false }: MessageInputProps) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showTemplatesMenu, setShowTemplatesMenu] = useState(false);
  const [showShortcutSuggestions, setShowShortcutSuggestions] = useState(false);
  const [matchedTemplates, setMatchedTemplates] = useState<AccountTemplate[]>([]);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const templatesMenuRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { templates, loading: templatesLoading, getTemplateByShortcut } = useTemplates();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
      if (templatesMenuRef.current && !templatesMenuRef.current.contains(event.target as Node)) {
        setShowTemplatesMenu(false);
      }
    };

    if (showEmojiPicker || showTemplatesMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker, showTemplatesMenu]);

  useEffect(() => {
    const lastWord = message.split(/\s/).pop() || '';

    if (lastWord.startsWith('/') && lastWord.length > 1) {
      const shortcut = lastWord;
      const matched = templates.filter(t =>
        t.shortcut && t.shortcut.toLowerCase().startsWith(shortcut.toLowerCase())
      );

      if (matched.length > 0) {
        console.log('📝 [MessageInput] Matched shortcuts:', matched.map(t => t.shortcut));
        setMatchedTemplates(matched);
        setShowShortcutSuggestions(true);
      } else {
        setShowShortcutSuggestions(false);
      }
    } else {
      setShowShortcutSuggestions(false);
    }
  }, [message, templates]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setMessage(message + emojiData.emoji);
    setShowEmojiPicker(false);
    textareaRef.current?.focus();
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const toggleTemplatesMenu = () => {
    setShowTemplatesMenu(!showTemplatesMenu);
  };

  const handleSelectTemplate = (template: AccountTemplate) => {
    console.log('✅ [MessageInput] Template selected:', template.name);
    setMessage(template.content);
    setShowTemplatesMenu(false);
    setShowShortcutSuggestions(false);
    textareaRef.current?.focus();
  };

  const handleSelectShortcutSuggestion = (template: AccountTemplate) => {
    console.log('✅ [MessageInput] Shortcut suggestion selected:', template.shortcut);
    const words = message.split(/\s/);
    words[words.length - 1] = template.content;
    setMessage(words.join(' '));
    setShowShortcutSuggestions(false);
    textareaRef.current?.focus();
  };

  const handleSendClick = () => {
    if (message.trim() && !sending) {
      onSendMessage();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!sending) {
        handleSendClick();
      }
    }
  };

  const handleImageClick = () => {
    imageInputRef.current?.click();
  };

  const handleDocumentClick = () => {
    documentInputRef.current?.click();
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione um arquivo de imagem válido');
      return;
    }

    const maxSize = 16 * 1024 * 1024; // 16MB
    if (file.size > maxSize) {
      alert('A imagem deve ter no máximo 16MB');
      return;
    }

    try {
      const base64 = await convertFileToBase64(file);
      await onSendImage(base64, '', file.name);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Erro ao enviar imagem:', error);
      alert('Erro ao enviar imagem. Tente novamente.');
    }
  };

  const handleDocumentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      alert('O documento deve ter no máximo 100MB');
      return;
    }

    try {
      const base64 = await convertFileToBase64(file);
      await onSendDocument(base64, file.name, '');
      if (documentInputRef.current) {
        documentInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Erro ao enviar documento:', error);
      alert('Erro ao enviar documento. Tente novamente.');
    }
  };

  return (
    <div className="p-4 relative">
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        className="hidden"
      />
      <input
        ref={documentInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar"
        onChange={handleDocumentChange}
        className="hidden"
      />
      <div className="flex flex-col gap-2">
        <div className="flex-1">
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite sua mensagem ou use /atalho"
            className="resize-none border-0 min-h-[80px]  "
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-2 text-neutral-50">
            <Smile
              className="w-4 h-4 cursor-pointer hover:text-neutral-300 transition-colors"
              onClick={toggleEmojiPicker}
            />
            <Popover>
              <PopoverTrigger>
                <Plus className="w-4 h-4 cursor-pointer hover:text-neutral-300 transition-colors" />
              </PopoverTrigger>
              <PopoverContent className="!bg-[#18181B] p-1 w-[95px] flex flex-col gap-1 border-[#3F3F464D] border">
                <button
                  onClick={handleImageClick}
                  disabled={sending}
                  className="w-full flex gap-2 p-1 justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px] disabled:opacity-50"
                >
                  <Images className="w-5 h-5" />
                  <span>Imagem</span>
                </button>
                <button
                  onClick={handleDocumentClick}
                  disabled={sending}
                  className="w-full flex gap-2  p-1 rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px] disabled:opacity-50"
                >
                  <File className=" w-5 h-5" />
                  <span>arquivo</span>
                </button>
              </PopoverContent>
            </Popover>

            <div className="relative">
              <FileText
                className="w-4 h-4 cursor-pointer hover:text-neutral-300 transition-colors"
                onClick={toggleTemplatesMenu}
              />
              {showTemplatesMenu && (
                <div ref={templatesMenuRef} className="absolute bottom-full left-0 mb-2 z-50">
                  <TemplatesMenu
                    templates={templates}
                    onSelectTemplate={handleSelectTemplate}
                    loading={templatesLoading}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleSendClick}
              disabled={!message.trim() || sending}
              className="bg-[#E4E4E7] min-w-[90px]"
            >
              {sending ? 'Enviando...' : 'Enviar'}
            </Button>
            <AudioRecorder onSendAudio={onSendAudio} disabled={sending} />
          </div>
        </div>
      </div>

      {showEmojiPicker && (
        <div ref={emojiPickerRef} className="absolute bottom-1/2 left-0 mb-2 z-50">
          <EmojiPicker onEmojiClick={handleEmojiClick} theme={Theme.DARK} />
        </div>
      )}

      {showShortcutSuggestions && matchedTemplates.length > 0 && (
        <div className="absolute bottom-full left-0 mb-2 w-full max-w-[360px] bg-neutral-800 rounded-lg shadow-xl border border-neutral-700 overflow-hidden z-50">
          <div className="p-2 border-b border-neutral-700">
            <p className="text-xs text-neutral-400">Pressione Tab ou clique para usar</p>
          </div>
          <div className="max-h-[200px] overflow-y-auto">
            {matchedTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleSelectShortcutSuggestion(template)}
                className="w-full text-left px-3 py-2 hover:bg-neutral-700 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-neutral-900 text-blue-400 border border-neutral-600">
                    {template.shortcut}
                  </span>
                  <span className="text-sm font-medium text-neutral-50">
                    {template.name}
                  </span>
                </div>
                <p className="text-xs text-neutral-400 line-clamp-2">
                  {template.content}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
